import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { useRead, ReadState, isVideos } from "@/components/LoadingStates";
import { useAuth } from "@/context/AuthContext";
import { isLiveVideo } from "@/components/LiveDemoBadge";
import IncludeDemoToggle from "@/components/IncludeDemoToggle";
import useIncludeDemo from "@/hooks/useIncludeDemo";


import VideoCard from "@/components/VideoCard";
import SavedTags, { savedTagsOf, tagKey } from "@/components/SavedTags";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

import { Bookmark } from "lucide-react";
import ExportButton from "@/components/ExportButton";

// GET /saved returns at most this many references; filters apply to those only.
const SAVED_READ_LIMIT = 500;

export default function Saved() {
  const { user, canEdit } = useAuth();
  const filterLabelId = useId();
  const [includeDemo, setIncludeDemo] = useIncludeDemo(user?.workspace_id);
  const videosRead = useRead("/saved", isVideos);
  const scope = videosRead.key;
  const rawVideos = videosRead.data || [];
  const demoVideos = rawVideos.filter((v) => includeDemo || isLiveVideo(v));
  // Tag choices come from the references left by the Demo/Live filter; the selection belongs to one read scope.
  const labels = new Map();
  demoVideos.forEach((v) => savedTagsOf(v).forEach((t) => { if (!labels.has(tagKey(t))) labels.set(tagKey(t), t.trim()); }));
  const availableTags = [...labels].map(([key, label]) => ({ key, label })).sort((a, b) => a.label.localeCompare(b.label, "fr"));
  const [selection, setSelection] = useState({ key: scope, tags: [] });
  const selected = (selection.key === scope ? selection.tags : []).filter((key) => labels.has(key));

  // Open tag editors, per reference, for this read scope only. A card whose editor is open stays displayed
  // even when it no longer matches the filters, so a draft and an uncertain outcome remain reachable.
  const [editorState, setEditorState] = useState({ key: scope, map: {} });
  const openEditors = editorState.key === scope ? editorState.map : {};
  const inflight = useRef(new Set());
  const currentScope = useRef(scope);
  currentScope.current = scope;
  const editors = useMemo(() => ({
    // False once the page shows another account, workspace or role than the one the editor was opened in.
    current: () => currentScope.current === scope,
    open: (id, init) => setEditorState((prev) => ({ key: scope, map: { ...(prev.key === scope ? prev.map : {}), [id]: { draft: "", error: "", uncertain: false, saving: false, ...init } } })),
    patch: (id, part) => setEditorState((prev) => (prev.key === scope && prev.map[id] ? { key: scope, map: { ...prev.map, [id]: { ...prev.map[id], ...part } } } : prev)),
    close: (id) => setEditorState((prev) => {
      if (prev.key !== scope || !prev.map[id]) return prev;
      const { [id]: closed, ...map } = prev.map;
      return { key: scope, map };
    }),
    begin: (id) => { const k = `${scope}|${id}`; if (inflight.current.has(k)) return false; inflight.current.add(k); return true; },
    end: (id) => { inflight.current.delete(`${scope}|${id}`); },
  }), [scope]);

  const matchesFilters = (v) => (includeDemo || isLiveVideo(v)) && selected.every((k) => savedTagsOf(v).map(tagKey).includes(k));
  const videos = rawVideos.filter((v) => matchesFilters(v) || openEditors[v.id]);
  const orphans = videosRead.status === "success" ? Object.entries(openEditors).filter(([id]) => !rawVideos.some((v) => v.id === id)) : [];
  const tagFiltered = selected.length > 0;

  // When a card leaves the display after its editor closes (confirmed save or cancel), focus moves to this status.
  const [notice, setNotice] = useState({ key: scope, text: "" });
  const statusRef = useRef(null);
  const closedRef = useRef(null);
  const announce = useCallback((text) => { setNotice({ key: scope, text }); statusRef.current?.focus(); }, [scope]);
  const onClosed = useCallback((id, title) => { closedRef.current = { id, title, scope }; }, [scope]);
  // Like a tag change, any Demo/Live change (toggle, empty-state button, another tab) makes the notice stale.
  // Declared before the announcement effect so that an announcement made in the same commit wins.
  useEffect(() => { setNotice((prev) => (prev.text ? { key: prev.key, text: "" } : prev)); }, [includeDemo]);
  useEffect(() => {
    const closed = closedRef.current;
    if (!closed) return;
    closedRef.current = null;
    if (closed.scope === scope && !videos.some((v) => v.id === closed.id)) announce(`« ${closed.title} » ne correspond pas aux filtres actifs : la carte n’est plus affichée.`);
  });
  const toggleTag = (key) => { setNotice({ key: scope, text: "" }); setSelection({ key: scope, tags: selected.includes(key) ? selected.filter((k) => k !== key) : [...selected, key] }); };
  const resetTags = () => { setNotice({ key: scope, text: "" }); setSelection({ key: scope, tags: [] }); };

  const { setData, reload } = videosRead;
  const recheck = useCallback(() => { reload().catch(() => {}); }, [reload]);
  // The server's answer is published as is: stored tags and note with their revisions. Without it, re-read.
  const updateTags = useCallback((videoId, answer) => {
    const meta = answer?.saved_meta;
    if (!meta || !Array.isArray(meta.tags)) { recheck(); return; }
    setData((list) => (Array.isArray(list) ? list.map((v) => (v.id === videoId ? { ...v, saved_tags: meta.tags, saved_note: meta.note, saved_revisions: meta.revisions } : v)) : list));
  }, [setData, recheck]);
  // Offered after a conflict: re-read, then base the kept draft on the tags read, shown to the user.
  // Nothing is sent; the user decides whether to save the draft over them.
  const reread = useCallback(async (videoId) => {
    let list;
    try { list = await reload(); } catch { return; }
    if (!editors.current()) return;
    const fresh = Array.isArray(list) ? list.find((v) => v.id === videoId) : null;
    if (fresh) editors.patch(videoId, { revision: fresh.saved_revisions?.tags, conflict: false, error: "", rebased: savedTagsOf(fresh) });
  }, [reload, editors]);

  return (
    <div className="space-y-6" data-testid="saved-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2">
            <Bookmark className="h-6 w-6 text-primary" />
            <h1 className="op-page-title">Saved Library</h1>
          </div>
          <p className="op-body text-muted-foreground mt-1.5">Your reference collection of interesting videos.</p>
        </div>
        <ExportButton path="/export/saved.csv" testid="export-saved-button" />
      </div>

      <IncludeDemoToggle checked={includeDemo} onChange={setIncludeDemo} />
      {videosRead.status === "success" && rawVideos.length > 0 && <div role="group" aria-labelledby={filterLabelId} className="space-y-2" data-testid="saved-tag-filter">
        <div className="flex flex-wrap items-center gap-2">
          <span id={filterLabelId} className="text-sm font-medium">Filtrer par tags</span>
          {availableTags.map((t) => <Button key={t.key} type="button" size="sm" variant={selected.includes(t.key) ? "default" : "outline"} aria-pressed={selected.includes(t.key)} onClick={() => toggleTag(t.key)} className="h-7 px-2.5 text-xs" data-testid={`saved-tag-filter-option-${t.key}`}>{t.label}</Button>)}
          {!availableTags.length && <span className="op-meta text-muted-foreground" data-testid="saved-tag-filter-none">Aucun tag parmi les références affichables.</span>}
          {tagFiltered && <Button type="button" size="sm" variant="ghost" onClick={resetTags} className="h-7 px-2.5 text-xs" data-testid="saved-tag-filter-reset">Réinitialiser le filtre de tags</Button>}
        </div>
        <p className="op-meta text-muted-foreground" data-testid="saved-tag-filter-scope">Affiche les références portant tous les tags sélectionnés, parmi les {rawVideos.length} reçues. Ce filtre agit sur l’affichage uniquement ; l’export CSV n’est pas filtré.</p>
        {rawVideos.length >= SAVED_READ_LIMIT && <p className="op-meta text-muted-foreground" data-testid="saved-read-limit">Le serveur renvoie au plus {SAVED_READ_LIMIT} références : les autres ne sont ni affichées ni filtrées ici.</p>}
      </div>}
      <p className="op-meta text-muted-foreground" data-testid="demo-filter-scope">{videos.length} éléments affichés sur {rawVideos.length} reçus.</p>
      <p ref={statusRef} tabIndex={-1} role="status" className="op-meta focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded" data-testid="saved-filter-notice">{notice.key === scope ? notice.text : ""}</p>
      {!includeDemo && <p className="op-meta text-muted-foreground" data-testid="demo-export-scope">Le filtre démo/live agit sur l’affichage ; l’export CSV conserve le périmètre serveur.</p>}
      {orphans.length > 0 && <section aria-label="Brouillons de tags en attente" className="space-y-2" data-testid="saved-orphan-drafts">
        {orphans.map(([id, editor]) => <div key={id} className="rounded-lg border border-border p-3 space-y-1.5 op-meta max-w-none" data-testid={`saved-orphan-draft-${id}`}>
          <p>« {editor.title} » n’apparaît plus dans les résultats reçus. Votre brouillon de tags est conservé ci-dessous ; il ne peut pas être enregistré ici.</p>
          <label className="block font-medium" htmlFor={`saved-orphan-input-${id}`}>Brouillon de tags de « {editor.title} »</label>
          <Input id={`saved-orphan-input-${id}`} readOnly value={editor.draft} className="h-8 text-xs md:text-xs" data-testid={`saved-orphan-input-${id}`} />
          {editor.error && <p className="text-destructive break-words" data-testid={`saved-orphan-error-${id}`}>{editor.error}</p>}
          <Button type="button" size="sm" variant="outline" onClick={() => { editors.close(id); announce(`Brouillon de « ${editor.title} » fermé.`); }} data-testid={`saved-orphan-close-${id}`}>Fermer le brouillon</Button>
        </div>)}
      </section>}
      <ReadState read={videosRead} scope="saved" empty={!videos.length} filtered={!!rawVideos.length}
        title={!rawVideos.length ? "Aucune vidéo sauvegardée" : tagFiltered ? "Aucune référence ne porte tous ces tags parmi les résultats reçus" : "Aucun contenu Live parmi les résultats reçus"}
        description={!rawVideos.length ? "Parcourez Signals et sauvegardez les vidéos qui vous intéressent." : tagFiltered ? "Retirez un tag de la sélection ou réinitialisez le filtre." : "Réincluez la démo pour afficher ces vidéos."}
        primaryCta={!rawVideos.length ? { label: "Parcourir Signals", to: "/outliers" } : tagFiltered ? { label: "Réinitialiser le filtre de tags", onClick: resetTags } : { label: "Réinclure la démo", onClick: () => setIncludeDemo(true) }}>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
          {videos.map((v) => <div key={v.id} className="min-w-0 space-y-1.5">
            <VideoCard video={v} />
            <SavedTags video={v} canEdit={canEdit} editor={openEditors[v.id]} editors={editors} onSaved={updateTags} onUncertain={recheck} onReread={reread} onClosed={onClosed} outsideFilter={!matchesFilters(v)} />
          </div>)}
        </div>
      </ReadState>
    </div>
  );
}
